import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiMessageSquare,
  FiX,
} from "react-icons/fi";
import axios from "../api/axiosInstance";

const DEFAULT_FILTERS = {
  search: "",
  source_id: "all",
  reply_status: "all",
  ai_status: "all",
  review_type: "all",
  should_reply: "all",
  reservation_linked: "all",
  sentiment_label: "all",
  date_from: "",
  date_to: "",
  rating_min: "",
  rating_max: "",
  sort_by: "review_date",
  sort_dir: "desc",
  page_size: 20,
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function toQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === "all") return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function formatDate(value) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatScore(value, scale) {
  const numericValue = Number(value);
  const numericScale = Number(scale);
  if (!Number.isFinite(numericValue) || !Number.isFinite(numericScale) || numericScale <= 0) {
    return "No score";
  }
  return `${numericValue}/${numericScale}`;
}

function formatNormalizedScore(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "Pending";
  return `${Math.round(numericValue)}/100`;
}

function getReplyLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "responded":
      return "Already answered";
    case "recommended":
      return "Recommended";
    case "draft_ready":
      return "Draft ready";
    case "dismissed":
      return "Dismissed";
    default:
      return "Pending";
  }
}

function getReplyTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "responded":
      return "success";
    case "recommended":
    case "draft_ready":
      return "warning";
    case "dismissed":
      return "muted";
    default:
      return "neutral";
  }
}

function getAnalysisLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "done":
      return "Ready";
    case "processing":
      return "In progress";
    case "error":
      return "Needs review";
    case "dismissed":
      return "Dismissed";
    default:
      return "Waiting";
  }
}

function getAnalysisTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "done":
      return "success";
    case "processing":
      return "warning";
    case "error":
      return "danger";
    case "dismissed":
      return "muted";
    default:
      return "neutral";
  }
}

function getSentimentLabel(label) {
  switch (String(label || "").toLowerCase()) {
    case "very_negative":
      return "Very negative";
    case "negative":
      return "Negative";
    case "neutral":
      return "Neutral";
    case "positive":
      return "Positive";
    case "very_positive":
      return "Very positive";
    default:
      return "Pending";
  }
}

function getSentimentTone(label) {
  switch (String(label || "").toLowerCase()) {
    case "very_negative":
    case "negative":
      return "danger";
    case "positive":
    case "very_positive":
      return "success";
    case "neutral":
      return "neutral";
    default:
      return "muted";
  }
}

function getReviewTypeLabel(review) {
  if (review?.is_internal_review) return "Internal survey";
  if (review?.manual_entry) return "Manual entry";
  if (review?.is_public_review) return "Public channel";
  return "Review";
}

function buildPillClass(baseClass, tone) {
  return `${baseClass} ${baseClass}--${tone}`;
}

function buildReviewSnippet(review) {
  const source = [review?.title, review?.review_text, review?.negative_text, review?.positive_text].find((value) =>
    String(value || "").trim(),
  );
  if (!source) return "No comment saved.";
  const normalized = String(source).trim().replace(/\s+/g, " ");
  return normalized.length > 150 ? `${normalized.slice(0, 147)}...` : normalized;
}

function formatJson(value) {
  if (!value) return "No extra source data saved.";
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value);
  }
}

function Modal({ open, title, subtitle, onClose, children, size = "default" }) {
  if (!open) return null;
  return (
    <div className="ar-modalBackdrop" onClick={onClose}>
      <div
        className={`ar-modal ar-modal--${size}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="ar-modalHeader">
          <div>
            <h2 className="ar-modalTitle">{title}</h2>
            {subtitle ? <p className="ar-modalSubtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="ar-iconBtn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="ar-modalBody">{children}</div>
      </div>
    </div>
  );
}

function DetailModal({ open, loading, error, review, onClose }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={review?.guest_display_name || review?.guest_name || "Review detail"}
      subtitle="Open the full review, what the system already knows about it, and the source traceability in one place."
      size="wide"
    >
      {loading ? <div className="ar-empty">Loading review detail...</div> : null}
      {!loading && error ? <div className="ar-notice ar-notice--error">{error}</div> : null}
      {!loading && !error && review ? (
        <div className="ar-detail">
          <div className="ar-summaryCards">
            <article className="ar-summaryCard">
              <div className="ar-summaryLabel">Channel</div>
              <div className="ar-summaryValue">{review?.source?.name || "Unknown channel"}</div>
              <div className="ar-summaryMeta">{getReviewTypeLabel(review)}</div>
            </article>
            <article className="ar-summaryCard">
              <div className="ar-summaryLabel">Review date</div>
              <div className="ar-summaryValue">{formatDate(review.review_date || review.created_at)}</div>
              <div className="ar-summaryMeta">{formatDateTime(review.published_at || review.created_at)}</div>
            </article>
            <article className="ar-summaryCard">
              <div className="ar-summaryLabel">Original score</div>
              <div className="ar-summaryValue">{formatScore(review.rating_value, review.rating_scale_max)}</div>
              <div className="ar-summaryMeta">{formatNormalizedScore(review.normalized_rating_100)}</div>
            </article>
            <article className="ar-summaryCard">
              <div className="ar-summaryLabel">Reply status</div>
              <div className="ar-summaryValue">{getReplyLabel(review.reply_status)}</div>
              <div className="ar-summaryMeta">{review.should_reply ? "Recommended to review" : "No urgent reply flag"}</div>
            </article>
          </div>

          <section className="ar-section">
            <div className="ar-sectionHeader">
              <div>
                <h3 className="ar-sectionTitle">What the guest wrote</h3>
                <p className="ar-sectionText">This is the original material saved for this review.</p>
              </div>
            </div>
            <div className="ar-grid">
              <article className="ar-card ar-card--span2">
                <div className="ar-cardLabel">Headline</div>
                <div className="ar-longText">{review.title || "No title provided."}</div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Guest name</div>
                <div className="ar-cardValue">{review.guest_display_name || review.guest_name || "Anonymous guest"}</div>
                <div className="ar-cardMeta">{review.author_location || "No location provided"}</div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Stay period</div>
                <div className="ar-cardValue">
                  {review.stay_date_from || review.stay_date_to
                    ? `${formatDate(review.stay_date_from)} to ${formatDate(review.stay_date_to)}`
                    : review.date_of_stay_text || "Not provided"}
                </div>
                <div className="ar-cardMeta">{review.trip_type || review.travel_group || "No travel profile"}</div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Positive comments</div>
                <div className="ar-longText">{review.positive_text || "No separate positive comments saved."}</div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Negative comments</div>
                <div className="ar-longText">{review.negative_text || "No separate complaints saved."}</div>
              </article>
              <article className="ar-card ar-card--span2">
                <div className="ar-cardLabel">Full review</div>
                <div className="ar-longText">{review.review_text || "No full review text saved."}</div>
              </article>
              <article className="ar-card ar-card--span2">
                <div className="ar-cardLabel">Existing hotel reply</div>
                <div className="ar-longText">{review.property_reply_text || "No hotel reply saved yet."}</div>
                {review.property_reply_date ? <div className="ar-cardMeta">Reply date: {formatDateTime(review.property_reply_date)}</div> : null}
              </article>
            </div>
          </section>

          <section className="ar-section">
            <div className="ar-sectionHeader">
              <div>
                <h3 className="ar-sectionTitle">Automatic analysis</h3>
                <p className="ar-sectionText">This area fills in when the background analysis completes.</p>
              </div>
            </div>
            <div className="ar-grid">
              <article className="ar-card">
                <div className="ar-cardLabel">Current analysis status</div>
                <div className="ar-cardValue">{getAnalysisLabel(review?.ai_processing_queue?.queue_status)}</div>
                <div className="ar-cardMeta">
                  {review?.ai_processing_queue?.trigger_reason
                    ? `Reason: ${review.ai_processing_queue.trigger_reason}`
                    : "The review is waiting in the analysis flow."}
                </div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Sentiment</div>
                <div className="ar-cardValue">{getSentimentLabel(review.sentiment_label)}</div>
                <div className="ar-cardMeta">
                  {review.sentiment_score !== null && review.sentiment_score !== undefined
                    ? `Score: ${review.sentiment_score}`
                    : "Numerical score not available yet"}
                </div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Urgency</div>
                <div className="ar-cardValue">
                  {review.urgency_score !== null && review.urgency_score !== undefined ? Math.round(Number(review.urgency_score)) : "Pending"}
                </div>
                <div className="ar-cardMeta">
                  {review.critical_points_score !== null && review.critical_points_score !== undefined
                    ? `Critical points: ${Math.round(Number(review.critical_points_score))}`
                    : "Criticality pending"}
                </div>
              </article>
              <article className="ar-card ar-card--span3">
                <div className="ar-cardLabel">Guest-facing summary</div>
                <div className="ar-longText">{review?.latest_ai_analysis?.summary_text || "No summary generated yet."}</div>
              </article>
              <article className="ar-card ar-card--span3">
                <div className="ar-cardLabel">Manager summary</div>
                <div className="ar-longText">{review?.latest_ai_analysis?.manager_summary_text || "No manager summary generated yet."}</div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Suggested reply</div>
                <div className="ar-longText">{review?.latest_ai_analysis?.suggested_reply_text || "No reply draft generated yet."}</div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Suggested internal action</div>
                <div className="ar-longText">
                  {review?.latest_ai_analysis?.suggested_internal_action || "No internal action suggested yet."}
                </div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Root cause idea</div>
                <div className="ar-longText">{review?.latest_ai_analysis?.root_cause_hypothesis || "Not available yet."}</div>
              </article>
            </div>
          </section>

          <section className="ar-section">
            <div className="ar-sectionHeader">
              <div>
                <h3 className="ar-sectionTitle">Detected topics and scores</h3>
                <p className="ar-sectionText">Imported scores and future detected themes live together here.</p>
              </div>
            </div>
            <div className="ar-grid">
              <article className="ar-card">
                <div className="ar-cardLabel">Imported metric scores</div>
                {Array.isArray(review.metric_scores) && review.metric_scores.length ? (
                  <div className="ar-stackList">
                    {review.metric_scores.map((metric) => (
                      <div key={metric.id} className="ar-stackItem">
                        <strong>{metric.metric_label || metric.metric_code}</strong>
                        <span>
                          {metric.score_raw !== null && metric.score_raw !== undefined ? `${metric.score_raw}` : "No raw score"}
                          {metric.score_normalized_100 !== null && metric.score_normalized_100 !== undefined
                            ? ` - ${Math.round(Number(metric.score_normalized_100))}/100`
                            : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ar-longText">No per-topic scores were imported for this review.</div>
                )}
              </article>
              <article className="ar-card ar-card--span2">
                <div className="ar-cardLabel">Detected topics</div>
                {Array.isArray(review.topics) && review.topics.length ? (
                  <div className="ar-topicList">
                    {review.topics.map((topicRow) => (
                      <div key={topicRow.id} className="ar-topicItem">
                        <div className="ar-topicHeader">
                          <strong>{topicRow?.topic?.label || topicRow?.topic?.code || "Topic"}</strong>
                          <span className={buildPillClass("ar-mini", getSentimentTone(topicRow?.polarity))}>
                            {String(topicRow?.polarity || "mixed").replace("_", " ")}
                          </span>
                        </div>
                        <div className="ar-cardMeta">
                          {topicRow?.topic?.topic_group || "Ungrouped"}
                          {topicRow?.confidence !== null && topicRow?.confidence !== undefined
                            ? ` - Confidence ${topicRow.confidence}`
                            : ""}
                        </div>
                        <div className="ar-longText">{topicRow.evidence_text || "No evidence excerpt saved."}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ar-longText">No topics have been saved yet for this review.</div>
                )}
              </article>
            </div>
          </section>

          <section className="ar-section">
            <div className="ar-sectionHeader">
              <div>
                <h3 className="ar-sectionTitle">Source traceability</h3>
                <p className="ar-sectionText">Operational users can ignore this section most of the time, but it is available when someone needs to audit where the review came from.</p>
              </div>
            </div>
            <div className="ar-grid">
              <article className="ar-card">
                <div className="ar-cardLabel">Review record</div>
                <div className="ar-stackList">
                  <div className="ar-stackItem"><strong>Reservation link</strong><span>{review.reservation_id ? `Linked to reservation ${review.reservation_id}` : "Not linked yet"}</span></div>
                  <div className="ar-stackItem"><strong>External review reference</strong><span>{review.external_review_id || "Not provided"}</span></div>
                  <div className="ar-stackItem"><strong>External reservation reference</strong><span>{review.external_reservation_id || "Not provided"}</span></div>
                  <div className="ar-stackItem"><strong>Language</strong><span>{review.original_language || "Not provided"}{review.detected_language ? ` - detected ${review.detected_language}` : ""}</span></div>
                  <div className="ar-stackItem"><strong>Saved on</strong><span>{formatDateTime(review.created_at)}</span></div>
                </div>
              </article>
              <article className="ar-card">
                <div className="ar-cardLabel">Import activity</div>
                <div className="ar-stackList">
                  <div className="ar-stackItem"><strong>Import method</strong><span>{review?.import_run?.run_type || (review.manual_entry ? "manual" : "not available")}</span></div>
                  <div className="ar-stackItem"><strong>File</strong><span>{review?.import_run?.file_name || "Manual entry or unavailable"}</span></div>
                  <div className="ar-stackItem"><strong>Import status</strong><span>{review?.import_run?.status || "Not available"}</span></div>
                  <div className="ar-stackItem"><strong>Reply log</strong><span>{review?.reply_tracking?.reply_channel_status || "No reply sync saved"}</span></div>
                </div>
              </article>
              <article className="ar-card ar-card--span3">
                <details className="ar-details">
                  <summary>Source payload and structured analysis</summary>
                  <div className="ar-detailsBody">
                    <div className="ar-codeBlock">{formatJson(review.raw_payload)}</div>
                    {review?.latest_ai_analysis?.structured_output ? (
                      <div className="ar-codeBlock">{formatJson(review.latest_ai_analysis.structured_output)}</div>
                    ) : null}
                  </div>
                </details>
              </article>
            </div>
          </section>
        </div>
      ) : null}
    </Modal>
  );
}

function AllReviewsPage() {
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const [businessUnit, setBusinessUnit] = useState(null);
  const [sources, setSources] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_items: 0,
    total_pages: 1,
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [selectedReviewDetail, setSelectedReviewDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchReviews = async ({ pageOverride, nextFilters, silent = false } = {}) => {
    const effectiveFilters = nextFilters || filters;
    silent ? setRefreshing(true) : setLoading(true);
    setError("");

    try {
      const query = toQueryString({
        business_unit_id: businessUnitId || undefined,
        page: pageOverride || 1,
        page_size: effectiveFilters.page_size,
        search: effectiveFilters.search,
        source_id: effectiveFilters.source_id,
        reply_status: effectiveFilters.reply_status,
        ai_status: effectiveFilters.ai_status,
        review_type: effectiveFilters.review_type,
        should_reply: effectiveFilters.should_reply,
        reservation_linked: effectiveFilters.reservation_linked,
        sentiment_label: effectiveFilters.sentiment_label,
        date_from: effectiveFilters.date_from,
        date_to: effectiveFilters.date_to,
        rating_min: effectiveFilters.rating_min,
        rating_max: effectiveFilters.rating_max,
        sort_by: effectiveFilters.sort_by,
        sort_dir: effectiveFilters.sort_dir,
      });
      const response = await axios.get(`/api/reviews-intelligence/reviews${query}`);
      setBusinessUnit(response.data?.business_unit || null);
      setSources(Array.isArray(response.data?.sources) ? response.data.sources : []);
      setReviews(Array.isArray(response.data?.reviews) ? response.data.reviews : []);
      setPagination(
        response.data?.pagination || {
          page: 1,
          page_size: effectiveFilters.page_size,
          total_items: 0,
          total_pages: 1,
        },
      );
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error loading reviews.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setSelectedReviewId(null);
    setSelectedReviewDetail(null);
    setDetailError("");
    fetchReviews({ nextFilters: DEFAULT_FILTERS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  const stats = useMemo(() => {
    const normalizedScores = reviews
      .map((review) => Number(review.normalized_rating_100))
      .filter((value) => Number.isFinite(value));
    const averageScore = normalizedScores.length
      ? Math.round(normalizedScores.reduce((sum, value) => sum + value, 0) / normalizedScores.length)
      : null;
    const needsReply = reviews.filter(
      (review) =>
        review?.should_reply ||
        ["recommended", "draft_ready"].includes(String(review?.reply_status || "").toLowerCase()),
    ).length;
    const waitingAnalysis = reviews.filter(
      (review) => !["done", "dismissed"].includes(String(review?.ai_queue_status || "").toLowerCase()),
    ).length;
    return {
      total: pagination.total_items || 0,
      averageScore,
      needsReply,
      waitingAnalysis,
    };
  }, [pagination.total_items, reviews]);

  const handleFilterChange = (field) => (event) => {
    setFilters((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleApplyFilters = async (event) => {
    event.preventDefault();
    await fetchReviews({ pageOverride: 1, nextFilters: filters });
  };

  const handleClearFilters = async () => {
    setFilters(DEFAULT_FILTERS);
    await fetchReviews({ pageOverride: 1, nextFilters: DEFAULT_FILTERS });
  };

  const handleOpenDetail = async (reviewId) => {
    setSelectedReviewId(reviewId);
    setSelectedReviewDetail(null);
    setDetailLoading(true);
    setDetailError("");

    try {
      const query = toQueryString({ business_unit_id: businessUnitId || undefined });
      const response = await axios.get(`/api/reviews-intelligence/reviews/${reviewId}${query}`);
      setSelectedReviewDetail(response.data?.review || null);
    } catch (requestError) {
      setDetailError(requestError?.response?.data?.error || "Error loading review detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedReviewId(null);
    setSelectedReviewDetail(null);
    setDetailError("");
    setDetailLoading(false);
  };

  const handlePageChange = async (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.total_pages || 1) || nextPage === pagination.page) return;
    await fetchReviews({ pageOverride: nextPage, nextFilters: filters, silent: true });
  };

  const handlePageSizeChange = async (event) => {
    const nextFilters = {
      ...filters,
      page_size: Number(event.target.value) || DEFAULT_FILTERS.page_size,
    };
    setFilters(nextFilters);
    await fetchReviews({ pageOverride: 1, nextFilters });
  };

  const pageStart = pagination.total_items > 0 ? ((pagination.page || 1) - 1) * (pagination.page_size || filters.page_size) + 1 : 0;
  const pageEnd = pagination.total_items > 0
    ? Math.min(pagination.total_items, ((pagination.page || 1) - 1) * (pagination.page_size || filters.page_size) + reviews.length)
    : 0;

  const hasActiveFilters = useMemo(() => (
    Object.entries(filters).some(([key, value]) => {
      if (key === "page_size") return false;
      if (key === "sort_by" && value === DEFAULT_FILTERS.sort_by) return false;
      if (key === "sort_dir" && value === DEFAULT_FILTERS.sort_dir) return false;
      return value !== "" && value !== "all";
    })
  ), [filters]);

  const uiCss = `
    .reservations-container.ri-pageShell{padding:12px;color:rgba(229,237,250,.92)}
    .reservations-container .ri-rates-card{position:relative;border-radius:16px;border:1px solid transparent;background:linear-gradient(180deg,rgba(25,25,51,.95),rgba(10,10,22,.95)) padding-box,linear-gradient(135deg,rgba(48,216,255,.52),rgba(168,85,247,.62),rgba(255,77,79,.35)) border-box;box-shadow:0 24px 70px rgba(0,0,0,.5);overflow:hidden;display:flex;flex-direction:column;gap:6px;padding:12px}
    .reservations-container .ri-rates-card::before{content:"";position:absolute;inset:0;background:radial-gradient(900px 300px at 8% -8%,rgba(48,216,255,.12),transparent 60%),radial-gradient(700px 280px at 95% 0%,rgba(168,85,247,.12),transparent 62%);pointer-events:none;z-index:0;opacity:.9}
    .reservations-container .ri-rates-card>*{position:relative;z-index:1}
    .reservations-container .rm-rates-header{padding:14px 16px 12px;border-bottom:1px solid rgba(255,255,255,.08)}
    .reservations-container .ri-toolbarRow{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap}
    .reservations-container .ri-titleBlock{display:flex;flex-direction:column;gap:4px;min-width:220px}
    .reservations-container .ri-moduleTag{display:inline-flex;align-items:center;gap:6px;font-size:.7rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.66)}
    .reservations-container .rm-rates-title{margin:0;font-size:1rem;font-weight:800;letter-spacing:.04px;color:rgba(233,239,251,.96)}
    .reservations-container .rm-rates-subtitle{margin-top:2px;font-size:.8rem;color:rgba(214,226,247,.68)}
    .reservations-container .ri-contextText{font-size:.74rem;color:rgba(214,226,247,.72)}
    .reservations-container .ri-toolbarActions{display:flex;gap:8px;flex-wrap:wrap;align-items:center;justify-content:flex-end}
    .reservations-container .rm-topBtn{border:1px solid rgba(255,255,255,.18);color:rgba(230,236,247,.92);background:rgba(255,255,255,.04);padding:7px 12px;border-radius:12px;font-size:.86rem;font-weight:700;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
    .reservations-container .rm-topBtn:hover{transform:translateY(-1px);background:rgba(255,255,255,.08);box-shadow:0 14px 22px rgba(0,0,0,.28)}
    .reservations-container .rm-topBtn:disabled{cursor:not-allowed;opacity:.6}
    .reservations-container .rm-topBtn--cyan{border-color:rgba(48,216,255,.58);color:rgba(196,239,255,.98);background:rgba(48,216,255,.11)}
    .reservations-container .rm-topBtn--green{border-color:rgba(43,220,136,.56);color:rgba(186,250,220,.98);background:rgba(43,220,136,.11)}
    .reservations-container .rm-topBtn--violet{border-color:rgba(153,126,255,.56);color:rgba(220,210,255,.98);background:rgba(153,126,255,.11)}
    .reservations-container .rm-topBtn--amber{border-color:rgba(255,193,95,.56);color:rgba(255,230,185,.98);background:rgba(255,193,95,.11)}
    .reservations-container .rm-searchInput{width:min(420px,40vw);min-width:220px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.14);color:rgba(230,230,247,.92);border-radius:10px;padding:8px 10px;outline:none;font-size:.9rem;transition:border-color 120ms ease,box-shadow 120ms ease,background 120ms ease}
    .reservations-container .rm-searchInput::placeholder{color:rgba(230,230,247,.45)}
    .reservations-container .rm-searchInput:focus{background:rgba(255,255,255,.06);border-color:rgba(48,216,255,.55);box-shadow:0 0 0 4px rgba(48,216,255,.12)}
    .reservations-container .ri-summaryPills{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}
    .reservations-container .ri-summaryPill{display:inline-flex;align-items:center;gap:8px;padding:5px 10px;border-radius:999px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.05);font-size:.72rem;color:rgba(229,237,250,.88)}
    .reservations-container .ri-summaryPill strong{font-size:.76rem;color:rgba(244,248,255,.98)}
    .reservations-container .ri-filterBar{margin-top:12px;padding-top:12px;border-top:1px solid rgba(255,255,255,.08);display:flex;flex-direction:column;gap:10px}
    .reservations-container .ri-filterGrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px}
    .reservations-container .ri-filterField{display:flex;flex-direction:column;gap:4px}
    .reservations-container .ri-filterLabel{font-size:.64rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:rgba(214,226,247,.62)}
    .reservations-container .ri-filterInput,.reservations-container .ri-filterSelect{width:100%;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.14);color:rgba(230,230,247,.92);border-radius:10px;padding:7px 9px;outline:none;font-size:.82rem}
    .reservations-container .ri-filterInput:focus,.reservations-container .ri-filterSelect:focus{background:rgba(255,255,255,.06);border-color:rgba(48,216,255,.55);box-shadow:0 0 0 4px rgba(48,216,255,.10)}
    .reservations-container .ri-filterActions{display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap}
    .reservations-container .ri-errorNotice{margin:10px 16px 0;padding:10px 12px;border-radius:10px;background:rgba(177,48,69,.18);border:1px solid rgba(255,125,147,.35);color:rgba(255,219,225,.96);font-size:.78rem}
    .reservations-container .ri-metaRow{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:8px;padding:0 16px}
    .reservations-container .ri-metaText{color:rgba(230,230,247,.62);font-size:.72rem}
    .reservations-container .ri-metaControls{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
    .reservations-container .ri-pageSizeSelect{background:rgba(255,255,255,.04);color:rgba(230,230,247,.88);border:1px solid rgba(255,255,255,.14);border-radius:4px;padding:2px 6px;font-size:.72rem}
    .reservations-container .ri-tableWrap{position:relative;flex:1 1 auto;min-height:0;scrollbar-gutter:stable both-edges;overscroll-behavior:contain;overflow-y:auto;overflow-x:auto;-webkit-overflow-scrolling:touch;padding:0 8px 8px}
    .reservations-container .ri-table{width:100%;min-width:1320px;border-collapse:separate;border-spacing:0;table-layout:fixed}
    .reservations-container .ri-table thead th{position:sticky;top:0;background:linear-gradient(180deg,rgba(22,22,50,.98),rgba(12,12,28,.96));color:rgba(231,240,255,.92);font-size:.66rem;font-weight:700;text-transform:none;letter-spacing:.02em;border-bottom:1px solid rgba(255,255,255,.12);padding:10px 12px;vertical-align:middle;z-index:3;text-align:left}
    .reservations-container .ri-table tbody tr{background:rgba(255,255,255,.012)}
    .reservations-container .ri-table tbody tr:nth-child(even){background:rgba(255,255,255,.03)}
    .reservations-container .ri-table tbody td{padding:10px 8px;font-size:.75rem;color:rgba(229,237,250,.9);border-bottom:1px solid rgba(255,255,255,.08);box-shadow:inset -1px 0 0 rgba(255,255,255,.03);vertical-align:middle;white-space:normal;overflow-wrap:break-word;line-height:1.25}
    .reservations-container .ri-table tbody tr:hover{background:rgba(48,216,255,.06)}
    .reservations-container .ri-cellTitle{font-size:.78rem;font-weight:700;color:rgba(244,248,255,.96)}
    .reservations-container .ri-cellText{margin-top:4px;font-size:.72rem;line-height:1.35;color:rgba(214,226,247,.74)}
    .reservations-container .ri-cellMeta{display:block;margin-top:4px;font-size:.68rem;color:rgba(214,226,247,.58);line-height:1.35}
    .reservations-container .ri-score{font-size:.82rem;font-weight:800;color:rgba(244,248,255,.97)}
    .reservations-container .ri-chip{display:inline-flex;align-items:center;padding:2px 8px;border-radius:999px;font-size:.68rem;font-weight:700;letter-spacing:.01em;border:1px solid rgba(255,255,255,.16)}
    .reservations-container .ri-chip--neutral{color:rgba(212,224,244,.95);background:rgba(255,255,255,.06)}
    .reservations-container .ri-chip--success{color:rgba(176,246,220,.98);background:rgba(25,164,122,.25);border-color:rgba(77,210,170,.45)}
    .reservations-container .ri-chip--warning{color:rgba(255,230,185,.98);background:rgba(255,193,95,.14);border-color:rgba(255,193,95,.35)}
    .reservations-container .ri-chip--danger{color:rgba(255,204,204,.98);background:rgba(177,48,69,.24);border-color:rgba(255,125,147,.42)}
    .reservations-container .ri-chip--muted{color:rgba(214,226,247,.65);background:rgba(255,255,255,.04);border-color:rgba(255,255,255,.08)}
    .reservations-container .ri-openBtn{padding:6px 10px;font-size:.78rem;display:inline-flex;align-items:center;gap:6px}
    .reservations-container .ri-empty{padding:28px;text-align:center;font-size:.8rem;line-height:1.7;color:rgba(214,226,247,.72)}
    .reservations-container .ri-pagination{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;padding:0 16px 8px}
    .reservations-container .ri-paginationText{font-size:.72rem;color:rgba(230,230,247,.62)}
    .reservations-container .ri-paginationControls{display:flex;gap:8px;align-items:center}
    .reservations-container .ri-pageBtn{border:1px solid rgba(255,255,255,.22);color:rgba(230,230,247,.78);background:transparent;padding:4px 8px;border-radius:6px;font-size:.72rem;font-weight:600;line-height:1.2}
    .reservations-container .ri-pageBtn:disabled{cursor:not-allowed;opacity:.35}
    .ar-modalBackdrop{position:fixed;inset:0;background:rgba(4,8,18,.68);backdrop-filter:blur(8px);display:flex;justify-content:center;align-items:flex-start;padding:34px 16px;z-index:1200;overflow-y:auto}
    .ar-modal{width:min(980px,100%);border-radius:24px;border:1px solid transparent;background:linear-gradient(180deg,rgba(24,24,48,.98),rgba(10,10,22,.98)) padding-box,linear-gradient(135deg,rgba(48,216,255,.45),rgba(168,85,247,.50),rgba(255,77,79,.28)) border-box;box-shadow:0 32px 80px rgba(10,17,20,.46);overflow:hidden;color:rgba(229,237,250,.92)}
    .ar-modal--wide{width:min(1240px,100%)}
    .ar-modalHeader{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px;border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.02)}
    .ar-modalTitle{margin:0;font-size:1.55rem;font-weight:800;color:rgba(244,248,255,.97)}
    .ar-modalSubtitle{margin:8px 0 0;font-size:.86rem;line-height:1.6;color:rgba(214,226,247,.68);max-width:880px}
    .ar-iconBtn{width:40px;height:40px;border-radius:12px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:rgba(230,236,247,.92);display:inline-flex;justify-content:center;align-items:center;cursor:pointer;font-size:18px}
    .ar-modalBody{padding:22px 24px 26px}
    .ar-detail{display:flex;flex-direction:column;gap:20px}
    .ar-summaryCards{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px}
    .ar-summaryCard,.ar-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:18px;padding:16px;box-shadow:none}
    .ar-summaryLabel,.ar-cardLabel{font-size:.68rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.62)}
    .ar-summaryValue,.ar-cardValue{margin-top:10px;font-size:1.02rem;font-weight:800;color:rgba(244,248,255,.96)}
    .ar-summaryMeta,.ar-cardMeta,.ar-sectionText{margin-top:6px;font-size:.8rem;color:rgba(214,226,247,.68);line-height:1.5}
    .ar-section{display:flex;flex-direction:column;gap:14px}
    .ar-sectionTitle{margin:0;font-size:1.05rem;font-weight:800;color:rgba(244,248,255,.96)}
    .ar-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px}
    .ar-card--span2{grid-column:span 2}
    .ar-card--span3{grid-column:span 3}
    .ar-longText{margin-top:10px;font-size:.85rem;line-height:1.7;color:rgba(229,237,250,.88);white-space:pre-wrap;word-break:break-word}
    .ar-stackList,.ar-topicList{display:flex;flex-direction:column;gap:12px;margin-top:12px}
    .ar-stackItem{display:flex;justify-content:space-between;gap:14px;flex-wrap:wrap;padding-bottom:12px;border-bottom:1px solid rgba(255,255,255,.08);font-size:.84rem;color:rgba(229,237,250,.88)}
    .ar-stackItem:last-child{padding-bottom:0;border-bottom:none}
    .ar-topicItem{padding:14px;border-radius:16px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08)}
    .ar-topicHeader{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}
    .ar-details summary{cursor:pointer;font-size:.85rem;font-weight:800;color:rgba(220,210,255,.96)}
    .ar-detailsBody{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-top:14px}
    .ar-codeBlock{border-radius:16px;background:rgba(6,9,18,.88);color:#f4f1ea;padding:16px;font-size:.75rem;line-height:1.6;overflow:auto;white-space:pre-wrap;word-break:break-word;max-height:360px;border:1px solid rgba(255,255,255,.08)}
    .ar-mini{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:5px 10px;font-size:.7rem;font-weight:800;white-space:nowrap}
    .ar-mini--neutral{background:rgba(255,255,255,.08);color:rgba(212,224,244,.95)}
    .ar-mini--success{background:rgba(25,164,122,.25);color:rgba(176,246,220,.98)}
    .ar-mini--warning{background:rgba(255,193,95,.14);color:rgba(255,230,185,.98)}
    .ar-mini--danger{background:rgba(177,48,69,.24);color:rgba(255,204,204,.98)}
    .ar-mini--muted{background:rgba(255,255,255,.04);color:rgba(214,226,247,.68)}
    .ar-notice{padding:12px 14px;border-radius:12px;font-size:.82rem;font-weight:600}
    .ar-notice--error{background:rgba(177,48,69,.18);border:1px solid rgba(255,125,147,.35);color:rgba(255,219,225,.96)}
    .ar-empty{padding:28px;text-align:center;font-size:.84rem;line-height:1.7;color:rgba(214,226,247,.72)}
    @media (max-width:1100px){.reservations-container .ri-filterGrid,.ar-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.ar-summaryCards{grid-template-columns:repeat(2,minmax(0,1fr))}.ar-card--span3{grid-column:span 2}}
    @media (max-width:860px){.reservations-container.ri-pageShell{padding:8px}.reservations-container .ri-toolbarRow,.reservations-container .ri-toolbarActions,.reservations-container .ri-pagination,.reservations-container .ri-metaRow,.ar-sectionHeader{flex-direction:column;align-items:stretch}.reservations-container .rm-searchInput{width:100%;min-width:0}.reservations-container .ri-filterGrid,.ar-summaryCards,.ar-grid,.ar-detailsBody{grid-template-columns:1fr}.ar-card--span2,.ar-card--span3{grid-column:span 1}.ar-modalBody,.ar-modalHeader{padding:18px}}
  `;

  return (
    <div className="reservations-container ri-pageShell">
      <style>{uiCss}</style>

      <section className="ri-rates-card">
        <div className="rm-rates-header">
          <div className="ri-toolbarRow">
            <div className="ri-titleBlock">
              <div className="ri-moduleTag">
                <FiMessageSquare />
                Review Intelligence
              </div>
              <h1 className="rm-rates-title">All Reviews</h1>
              <div className="rm-rates-subtitle">
                Central review list for daily follow-up. The list stays in focus, filters stay secondary.
              </div>
              {businessUnit?.name ? <div className="ri-contextText">Current hotel: {businessUnit.name}</div> : null}
            </div>

            <form className="ri-toolbarActions" onSubmit={handleApplyFilters}>
              <input
                className="rm-searchInput"
                type="text"
                placeholder="Search guest, title, text or channel..."
                value={filters.search}
                onChange={handleFilterChange("search")}
              />
              <button type="submit" className="rm-topBtn rm-topBtn--cyan" disabled={loading}>
                Search
              </button>
              <button
                type="button"
                className="rm-topBtn rm-topBtn--violet"
                onClick={() => setShowFilters((current) => !current)}
              >
                {showFilters ? "Hide Filters" : "Filters"}
              </button>
              <button
                type="button"
                className="rm-topBtn rm-topBtn--green"
                onClick={() => fetchReviews({ pageOverride: pagination.page || 1, nextFilters: filters, silent: true })}
                disabled={loading || refreshing}
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            </form>
          </div>

          <div className="ri-summaryPills">
            <span className="ri-summaryPill"><strong>{stats.total}</strong> reviews found</span>
            <span className="ri-summaryPill"><strong>{stats.averageScore === null ? "--" : `${stats.averageScore}/100`}</strong> average score</span>
            <span className="ri-summaryPill"><strong>{stats.needsReply}</strong> need reply attention</span>
            <span className="ri-summaryPill"><strong>{stats.waitingAnalysis}</strong> waiting analysis</span>
          </div>

          {showFilters ? (
            <form className="ri-filterBar" onSubmit={handleApplyFilters}>
              <div className="ri-filterGrid">
                <label className="ri-filterField">
                  <span className="ri-filterLabel">Channel</span>
                  <select className="ri-filterSelect" value={filters.source_id} onChange={handleFilterChange("source_id")}>
                    <option value="all">All channels</option>
                    {sources.map((source) => (
                      <option key={source.id} value={String(source.id)}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Reply</span>
                  <select className="ri-filterSelect" value={filters.reply_status} onChange={handleFilterChange("reply_status")}>
                    <option value="all">Any</option>
                    <option value="pending">Pending</option>
                    <option value="recommended">Recommended</option>
                    <option value="draft_ready">Draft ready</option>
                    <option value="responded">Answered</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Analysis</span>
                  <select className="ri-filterSelect" value={filters.ai_status} onChange={handleFilterChange("ai_status")}>
                    <option value="all">Any</option>
                    <option value="pending">Waiting</option>
                    <option value="processing">In progress</option>
                    <option value="done">Ready</option>
                    <option value="error">Needs review</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Type</span>
                  <select className="ri-filterSelect" value={filters.review_type} onChange={handleFilterChange("review_type")}>
                    <option value="all">All</option>
                    <option value="public">Public</option>
                    <option value="internal">Internal survey</option>
                    <option value="manual">Manual</option>
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Should reply</span>
                  <select className="ri-filterSelect" value={filters.should_reply} onChange={handleFilterChange("should_reply")}>
                    <option value="all">Either</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Reservation linked</span>
                  <select className="ri-filterSelect" value={filters.reservation_linked} onChange={handleFilterChange("reservation_linked")}>
                    <option value="all">Either</option>
                    <option value="true">Linked</option>
                    <option value="false">Not linked</option>
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Sentiment</span>
                  <select className="ri-filterSelect" value={filters.sentiment_label} onChange={handleFilterChange("sentiment_label")}>
                    <option value="all">Any</option>
                    <option value="very_negative">Very negative</option>
                    <option value="negative">Negative</option>
                    <option value="neutral">Neutral</option>
                    <option value="positive">Positive</option>
                    <option value="very_positive">Very positive</option>
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">From date</span>
                  <input className="ri-filterInput" type="date" value={filters.date_from} onChange={handleFilterChange("date_from")} />
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">To date</span>
                  <input className="ri-filterInput" type="date" value={filters.date_to} onChange={handleFilterChange("date_to")} />
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Min score</span>
                  <input className="ri-filterInput" type="number" min="0" max="100" value={filters.rating_min} onChange={handleFilterChange("rating_min")} />
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Max score</span>
                  <input className="ri-filterInput" type="number" min="0" max="100" value={filters.rating_max} onChange={handleFilterChange("rating_max")} />
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Sort by</span>
                  <select className="ri-filterSelect" value={filters.sort_by} onChange={handleFilterChange("sort_by")}>
                    <option value="review_date">Review date</option>
                    <option value="created_at">Saved date</option>
                    <option value="normalized_rating_100">Unified score</option>
                    <option value="guest">Guest name</option>
                  </select>
                </label>

                <label className="ri-filterField">
                  <span className="ri-filterLabel">Order</span>
                  <select className="ri-filterSelect" value={filters.sort_dir} onChange={handleFilterChange("sort_dir")}>
                    <option value="desc">Newest first</option>
                    <option value="asc">Oldest first</option>
                  </select>
                </label>
              </div>

              <div className="ri-filterActions">
                {hasActiveFilters ? (
                  <button type="button" className="rm-topBtn" onClick={handleClearFilters}>
                    Clear filters
                  </button>
                ) : null}
                <button type="submit" className="rm-topBtn rm-topBtn--cyan" disabled={loading}>
                  Apply filters
                </button>
              </div>
            </form>
          ) : null}
        </div>

        {error ? <div className="ri-errorNotice">{error}</div> : null}

        <div className="ri-metaRow">
          <small className="ri-metaText">{`Showing ${pageStart}-${pageEnd} of ${pagination.total_items || 0}`}</small>

          <div className="ri-metaControls">
            <label htmlFor="reviews-page-size" className="ri-metaText">size</label>
            <select
              id="reviews-page-size"
              className="ri-pageSizeSelect"
              value={String(filters.page_size)}
              onChange={handlePageSizeChange}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={String(size)}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="ri-tableWrap">
          {loading ? (
            <div className="ri-empty">Loading reviews...</div>
          ) : reviews.length ? (
            <table className="ri-table">
              <colgroup>
                <col style={{ width: "115px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "180px" }} />
                <col style={{ width: "360px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "90px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Channel</th>
                  <th>Guest</th>
                  <th>Review</th>
                  <th>Score</th>
                  <th>Sentiment</th>
                  <th>Reply</th>
                  <th>Analysis</th>
                  <th>Open</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review.id}>
                    <td>
                      <div className="ri-cellTitle">{formatDate(review.review_date || review.created_at)}</div>
                      <span className="ri-cellMeta">{formatDateTime(review.created_at)}</span>
                    </td>
                    <td>
                      <div className="ri-cellTitle">{review?.source?.name || "Unknown channel"}</div>
                      <span className="ri-cellMeta">{getReviewTypeLabel(review)}</span>
                    </td>
                    <td>
                      <div className="ri-cellTitle">{review.guest_display_name || review.guest_name || "Anonymous guest"}</div>
                      <span className="ri-cellMeta">{review.reservation_id ? "Reservation linked" : "No reservation linked"}</span>
                    </td>
                    <td>
                      <div className="ri-cellTitle">{review.title || "Guest comment"}</div>
                      <div className="ri-cellText">{buildReviewSnippet(review)}</div>
                    </td>
                    <td>
                      <div className="ri-score">{formatScore(review.rating_value, review.rating_scale_max)}</div>
                      <span className="ri-cellMeta">{formatNormalizedScore(review.normalized_rating_100)}</span>
                    </td>
                    <td>
                      <span className={buildPillClass("ri-chip", getSentimentTone(review.sentiment_label))}>
                        {getSentimentLabel(review.sentiment_label)}
                      </span>
                    </td>
                    <td>
                      <span className={buildPillClass("ri-chip", getReplyTone(review.reply_status))}>
                        {getReplyLabel(review.reply_status)}
                      </span>
                      {review.should_reply ? <span className="ri-cellMeta">Worth checking</span> : null}
                    </td>
                    <td>
                      <span className={buildPillClass("ri-chip", getAnalysisTone(review.ai_queue_status))}>
                        {getAnalysisLabel(review.ai_queue_status)}
                      </span>
                      {review.ai_trigger_reason ? <span className="ri-cellMeta">{review.ai_trigger_reason}</span> : null}
                    </td>
                    <td>
                      <button type="button" className="rm-topBtn rm-topBtn--violet ri-openBtn" onClick={() => handleOpenDetail(review.id)}>
                        <FiEye />
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="ri-empty">
              No reviews match the current filters.
              <br />
              Clear the filters or add more reviews from <strong>Review Import</strong>.
            </div>
          )}
        </div>

        <div className="ri-pagination">
          <div className="ri-paginationText">
            Page <strong>{pagination.page || 1}</strong> of <strong>{pagination.total_pages || 1}</strong>
          </div>
          <div className="ri-paginationControls">
            <button
              type="button"
              className="ri-pageBtn"
              onClick={() => handlePageChange((pagination.page || 1) - 1)}
              disabled={loading || refreshing || (pagination.page || 1) <= 1}
            >
              <FiChevronLeft /> Previous
            </button>
            <button
              type="button"
              className="ri-pageBtn"
              onClick={() => handlePageChange((pagination.page || 1) + 1)}
              disabled={loading || refreshing || (pagination.page || 1) >= (pagination.total_pages || 1)}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>
      </section>

      <DetailModal
        open={Boolean(selectedReviewId)}
        loading={detailLoading}
        error={detailError}
        review={selectedReviewDetail}
        onClose={closeDetail}
      />
    </div>
  );
}

export default AllReviewsPage;
